import { apiRequests, mapApiRequests } from '../utilities/axios-factory'
import store from '../store'
import { sleep } from '@smarttransit/common'

function getStRoute ({ id, include = null, includeArchived = false }) {
  const params = include ? { include } : null

  return apiRequests.get(`/v1/${includeArchived ? 'raw-' : ''}st-route/` + id + '/', {
    params,
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function searchOtpRoutes ({ routeId, routeParams, timeoutMs = 30000, requestTimeoutMs }) {
  // NOTE:
  // - timeoutMs controls the overall polling window (how long we'll wait for OTP to finish).
  // - requestTimeoutMs controls the per-HTTP-request timeout (axios), to avoid premature failures
  //   on slow OTP route computations.
  const resolvedRequestTimeoutMs = typeof requestTimeoutMs === 'number'
    ? requestTimeoutMs
    : Math.max(15000, Math.min(60000, timeoutMs))

  const result = await mapApiRequests.post(`/v1/geo-location/routes/${routeId}/`, { routeParams }, {
    timeout: resolvedRequestTimeoutMs,
    headers: { 'Authorization': store.getters.getTokenId }
  })

  const referenceId = result?.data?.id

  if (!referenceId) {
    throw new Error('No referenceId returned in OTP route search request')
  }

  let searchResults = null
  const pollingStartTime = Date.now()

  do {
    searchResults = await mapApiRequests.get(`/v1/geo-location/routes/${referenceId}/`, {
      timeout: resolvedRequestTimeoutMs,
      headers: { 'Authorization': store.getters.getTokenId }
    })

    if (searchResults?.data?.pending && Date.now() - pollingStartTime > timeoutMs) {
      throw new Error('OTP route search request timed out')
    }

    await sleep(800)
  } while (searchResults?.data?.pending)

  return searchResults?.data
}

function getStRouteTemplate () {
  return {
    label: '',
    agencyId: null,
    routeIdList: null
  }
}

function createStRoute ({ routeIdList, routeLine, agencyId, label, flatRateFare }) {
  return apiRequests.post(`/v1/st-route/`, { routeIdList, routeLine, agencyId, label, flatRateFare }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function updateStRoute ({ id, routeIdList, routeLine, agencyId, label, routeFares, flatRateFare }) {
  return apiRequests.patch(`/v1/st-route/${id}/`, { routeIdList, routeLine, agencyId, label, routeFares, flatRateFare }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function findStRoutes ({ keywords, routeIdList, agencyId, dateFrom, dateTo, offset, limit, order, include, archived = false }) {
  return apiRequests.get(`/v1/${archived ? 'raw-' : ''}st-route/`, {
    params: { keywords, routeIdList, agencyId, dateFrom, dateTo, limit, include, offset, order },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function findTotalStRoutes ({ keywords, routeIdList, agencyId, dateFrom, dateTo, archived = false }) {
  return apiRequests.get(`/v1/${archived ? 'raw-' : ''}st-route/count/`, {
    params: { keywords, routeIdList, agencyId, dateFrom, dateTo },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function deleteStRoute (id) {
  return apiRequests.delete('/v1/st-route/' + id + '/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function archiveStRoute (id) {
  return apiRequests.patch('/v1/st-route/' + id + '/archive/', {}, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function unArchiveStRoute (id) {
  return apiRequests.patch('/v1/st-route/' + id + '/unarchived/', {}, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function startGeneratePermutations (stRouteId) {
  return apiRequests.post(`/v1/st-route/${stRouteId}/permutations/`, {}, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function stopGeneratePermutations (stRouteId) {
  return apiRequests.delete(`/v1/st-route/${stRouteId}/permutations/`, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function getPermutationsJob (stRouteId, { includeArchived = false } = {}) {
  return apiRequests.get(`/v1/${includeArchived ? 'raw-' : ''}st-route/${stRouteId}/permutations/`, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function getRouteFaresByRouteId (stRouteId, { includeArchived = false } = {}) {
  return apiRequests.get(`/v1/${includeArchived ? 'raw-' : ''}st-route/${stRouteId}/route-fares/`, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export {
  getStRoute,
  getStRouteTemplate,
  createStRoute,
  updateStRoute,
  deleteStRoute,
  archiveStRoute,
  unArchiveStRoute,
  findStRoutes,
  findTotalStRoutes,
  startGeneratePermutations,
  stopGeneratePermutations,
  getPermutationsJob,
  getRouteFaresByRouteId,
  searchOtpRoutes
}
